import { FloatingDevice, LandingBackground, LandingBackgroundType } from '../types'

/**
 * Efectos de fondo opcionales.
 *
 * El valor por defecto es `normal`, es decir NINGÚN efecto: todas las landings
 * existentes y nuevas siguen exactamente como están hasta que el usuario elija
 * un efecto en el constructor. Los efectos son CSS puro (gradientes,
 * pseudo-elementos y animaciones sobre `transform`/`opacity`), sin librerías,
 * sin canvas, sin WebGL, sin vídeo y sin listeners.
 */
export const BACKGROUND_TYPES: {
  value: LandingBackgroundType
  label: string
  hint: string
}[] = [
  {
    value: 'normal',
    label: 'Normal / Actual',
    hint: 'Sin efecto ni capa añadida. Exactamente el fondo de ahora.',
  },
  {
    value: 'chispas_minimal',
    label: 'Chispas Tech — Minimal',
    hint: 'Micropartículas diminutas que se desplazan muy despacio y destellan.',
  },
  {
    value: 'chispas_neural',
    label: 'Chispas Tech — Neural',
    hint: 'Las mismas chispas más un recorrido luminoso finísimo y ocasional.',
  },
  {
    value: 'chispas_aura',
    label: 'Chispas Tech — Aura',
    hint: 'Chispas protagonistas con dos halos azulados muy tenues.',
  },
  /* Efectos anteriores del constructor: se conservan tal cual. */
  {
    value: 'destellos',
    label: 'Destellos sutiles',
    hint: 'Puntos muy finos con movimiento lento.',
  },
  {
    value: 'red',
    label: 'Red tecnológica',
    hint: 'Líneas y nodos apenas perceptibles.',
  },
  {
    value: 'aura',
    label: 'Aura digital',
    hint: 'Radiales suaves que se desplazan.',
  },
  {
    value: 'grid',
    label: 'Grid futurista',
    hint: 'Cuadrícula finísima con parallax mínimo.',
  },
]

/**
 * Ajustes iniciales al ELEGIR un efecto en el constructor.
 *
 * Los fondos Chispas venían casi invisibles con los valores generales
 * (intensidad baja, opacidad 6 %, velocidad muy lenta). Al seleccionarlos se
 * aplican unos valores en los que el movimiento se percibe desde el primer
 * momento, sin tener que subir los controles a mano. Los efectos anteriores
 * conservan exactamente los valores de siempre.
 */
export function backgroundPreset(
  type: LandingBackgroundType,
): Partial<LandingBackground> {
  if (type.startsWith('chispas')) {
    return {
      intensity: 'media',
      density: type === 'chispas_neural' ? 'media' : 'baja',
      speed: 'lenta',
      opacity: 16,
      motion: true,
    }
  }
  return {}
}

export const DEFAULT_BACKGROUND: LandingBackground = {
  type: 'normal',
  intensity: 'baja',
  density: 'baja',
  speed: 'muy_lenta',
  opacity: 6,
  motion: true,
  devices: ['desktop', 'tablet', 'mobile'],
}

/** Completa solo las claves ausentes, conservando cualquier valor guardado. */
export function withBackgroundDefaults(value?: unknown): LandingBackground {
  const config = (value ?? {}) as LandingBackground
  return {
    ...DEFAULT_BACKGROUND,
    ...config,
    type: config.type ?? 'normal',
    motion: config.motion ?? true,
    devices:
      config.devices && config.devices.length > 0
        ? config.devices
        : (['desktop', 'tablet', 'mobile'] as FloatingDevice[]),
  }
}

/**
 * Clases del efecto para el contenedor del fondo. Devuelve cadena vacía
 * cuando no hay efecto, de modo que no se monta ninguna capa extra.
 */
export function backgroundClass(config?: LandingBackground): string {
  const value = config?.type ?? 'normal'
  if (value === 'normal') return ''
  const devices = config?.devices ?? ['desktop', 'tablet', 'mobile']
  return [
    'ava-bg',
    `ava-bg--${value}`,
    `ava-bg--${config?.intensity ?? 'baja'}`,
    `ava-bg--d-${config?.density ?? 'baja'}`,
    `ava-bg--${config?.speed ?? 'muy_lenta'}`,
    config?.motion === false ? 'ava-bg--static' : '',
    devices.includes('mobile') ? '' : 'ava-bg--no-mobile',
    devices.includes('tablet') ? '' : 'ava-bg--no-tablet',
    devices.includes('desktop') ? '' : 'ava-bg--no-desktop',
  ]
    .filter(Boolean)
    .join(' ')
}
